import { type MouseEvent, type ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { useIsPhone } from '@/hooks/useIsPhone'

export interface Column<T> {
  key: string
  label: string
  render: (item: T) => ReactNode
  className?: string
}

interface AdminTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyFn: (item: T) => string | number
  /**
   * Makes the whole record a click target that opens this address. A pointer
   * convenience only: keyboard and screen-reader users reach the same page
   * through a real link inside the row.
   */
  rowHref?: (item: T) => string
}

const EMPTY = 'No records found'
const ROW_LINK = 'cursor-pointer transition-colors duration-150 hover:bg-surface-bg/60'

/** A click on a link or button inside the row belongs to that control, not the row. */
function openRow(event: MouseEvent, href: string) {
  if ((event.target as HTMLElement).closest('a, button, input, select, textarea')) return
  router.visit(href)
}

/**
 * Admin records in two shapes. On a wide screen they are a table; on a phone each
 * record stacks into a labelled card, because a seven-column table inside a 390px
 * screen is a horizontal scroller nobody can read. Columns are declared once and
 * rendered by whichever shape is in play — only one is ever in the DOM, so queries
 * and screen readers never meet the same record twice.
 */
export function AdminTable<T>({ columns, data, keyFn, rowHref }: AdminTableProps<T>) {
  const isPhone = useIsPhone()

  if (isPhone) {
    return (
      <ul data-testid="admin-cards" className="space-y-3">
        {data.map((item) => (
          <li
            key={keyFn(item)}
            onClick={rowHref && ((event) => openRow(event, rowHref(item)))}
            className={`rounded-lg border border-border-divider bg-surface-bg/40 px-4 py-3 divide-y divide-line-soft ${rowHref ? ROW_LINK : ''}`}
          >
            {columns.map((col) => (
              <div
                key={col.key}
                className="flex items-baseline justify-between gap-4 py-2 first:pt-0 last:pb-0"
              >
                <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted">
                  {col.label}
                </span>
                <span className="min-w-0 text-right text-sm font-light text-body">
                  {col.render(item)}
                </span>
              </div>
            ))}
          </li>
        ))}
        {data.length === 0 && (
          <li className="rounded-lg border border-border-divider px-4 py-8 text-center text-muted font-light">
            {EMPTY}
          </li>
        )}
      </ul>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border-divider">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-divider bg-surface-bg">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted ${col.className ?? ''}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-divider">
          {data.map((item) => (
            <tr
              key={keyFn(item)}
              onClick={rowHref && ((event) => openRow(event, rowHref(item)))}
              className={`font-light text-body ${rowHref ? ROW_LINK : ''}`}
            >
              {columns.map((col) => (
                <td key={col.key} className={`px-4 py-3 ${col.className ?? ''}`}>
                  {col.render(item)}
                </td>
              ))}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-muted font-light"
              >
                {EMPTY}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
